import React, { useState, useEffect, useMemo } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Autocomplete,
    Box,
    Typography,
    Divider,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Chip,
    Collapse,
    CircularProgress,
} from '@mui/material';
import {
    Folder as FolderIcon,
    Description as ProjectIcon,
    Task as TaskIcon,
    ExpandMore,
    ChevronRight,
} from '@mui/icons-material';
import { getAccounts, getAccountGroups } from '../redux/ducks/account';
import { getGroupTemplate, getProjectTemplate } from '../redux/ducks/template';
import { getBillingEmployees } from '../redux/ducks/billingEmployees';
import { useDispatch } from 'react-redux';
import AppDatePicker from './AppDatePicker';
import styled from '@emotion/styled';
import { formatDayShort, toLocalDay } from '../utils/dateUtils';

const ContentStack = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(2),
}));

const PreviewDivider = styled(Divider)(({ theme }) => ({
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
}));

const PreviewBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
}));

const StructureBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(1),
}));

const TemplateVarsHelper = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1.5),
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const StructureTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(1),
}));

const StructureItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'indent' })(({ theme, indent }) => ({
    paddingLeft: indent ? theme.spacing(indent) : 0,
    marginBottom: theme.spacing(0.5),
}));

const StructureClickableItem = styled(ListItem, { shouldForwardProp: (prop) => prop !== 'indent' && prop !== 'clickable' })(({ theme, indent, clickable }) => ({
    paddingLeft: indent ? theme.spacing(indent) : 0,
    marginBottom: theme.spacing(0.5),
    cursor: clickable ? 'pointer' : 'default',
}));

const SmallIcon = styled(ListItemIcon)({
    minWidth: 28,
});

const TinyIcon = styled(ListItemIcon)({
    minWidth: 20,
});

const InlineRow = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

const TypeChip = styled(Chip)(({theme}) => ({
    height: 18,
    fontSize: theme.typography.size.xs,
}));

const SmallExpandIcon = styled(ExpandMore)(({theme}) => ({
    fontSize: theme.typography.size.sm,
}));

const SmallChevronIcon = styled(ChevronRight)(({theme}) => ({
    fontSize: theme.typography.size.sm,
}));

const CustomVarField = styled(TextField)(({ theme }) => ({
    marginBottom: theme.spacing(1.5),
}));

function parseDateInput(value) {
    if (!value) return null;
    const [y, m, d] = value.split('-').map((x) => parseInt(x, 10));
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d, 0, 0, 0, 0);
}

function processTemplateString(str, vars, customVars = {}) {
    if (!str) return str;
    let result = str;
    result = result.replace(/{account_name}/ig, vars.accountName || '');
    result = result.replace(/{account_slug}/ig, vars.accountSlug || '');
    result = result.replace(/{month}/ig, vars.month || '');
    result = result.replace(/{month_short}/ig, vars.monthShort || '');
    result = result.replace(/{month_number}/ig, vars.monthNumber || '');
    result = result.replace(/{year}/ig, vars.year || '');
    result = result.replace(/{year_short}/ig, vars.yearShort || '');
    result = result.replace(/{day}/ig, vars.day || '');
    result = result.replace(/{quarter}/ig, vars.quarter || '');
    result = result.replace(/{date}/ig, vars.date || '');
    // Replace custom variables
    result = result.replace(/\{(custom_[a-zA-Z0-9_]+)\}/gi, (match, varName) => {
        return customVars[varName.toLowerCase()] || match;
    });
    return result;
}

function resolveRelativeAnchor(anchor, execDate) {
    const d = new Date(execDate);
    const year = d.getFullYear();
    const month = d.getMonth();
    const quarter = Math.floor(month / 3);

    switch (anchor) {
        case 'startOfMonth': return new Date(year, month, 1);
        case 'endOfMonth': return new Date(year, month + 1, 0);
        case 'startOfNextMonth': return new Date(year, month + 1, 1);
        case 'endOfNextMonth': return new Date(year, month + 2, 0);
        case 'startOfQuarter': return new Date(year, quarter * 3, 1);
        case 'endOfQuarter': return new Date(year, quarter * 3 + 3, 0);
        default: return null;
    }
}

const RELATIVE_ANCHOR_LABELS = {
    startOfMonth: 'Start of month',
    endOfMonth: 'End of month',
    startOfNextMonth: 'Start of next month',
    endOfNextMonth: 'End of next month',
    startOfQuarter: 'Start of quarter',
    endOfQuarter: 'End of quarter',
};

/**
 * Scan text for {custom_*} placeholders and return unique variable names.
 */
function extractCustomVars(texts) {
    const vars = new Set();
    const regex = /\{(custom_[a-zA-Z0-9_]+)\}/gi;
    texts.forEach((text) => {
        if (!text) return;
        let match;
        while ((match = regex.exec(text)) !== null) {
            vars.add(match[1].toLowerCase());
        }
    });
    return [...vars].sort();
}

/**
 * Collect all text fields from a full template tree (names + overviews of root, projects, tasks).
 */
function collectTemplateTexts(tmpl) {
    const texts = [tmpl?.name, tmpl?.overview];
    (tmpl?.projects || []).forEach((p) => {
        texts.push(p.name, p.overview);
        (p.tasks || []).forEach((t) => {
            texts.push(t.name, t.overview);
        });
    });
    (tmpl?.tasks || []).forEach((t) => {
        texts.push(t.name, t.overview);
    });
    return texts;
}

function formatCustomVarLabel(varName) {
    return varName.replace(/^custom_/, '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function collectUsedSlots(tmpl) {
    const slots = new Set();
    if (tmpl?.responsibleSlot) slots.add(tmpl.responsibleSlot);
    (tmpl?.projects || []).forEach((p) => {
        if (p.responsibleSlot) slots.add(p.responsibleSlot);
        (p.tasks || []).forEach((t) => { if (t.responsibleSlot) slots.add(t.responsibleSlot); });
    });
    (tmpl?.tasks || []).forEach((t) => { if (t.responsibleSlot) slots.add(t.responsibleSlot); });
    return [...slots].sort((a, b) => a - b);
}

const EmployeeSection = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
}));

const TemplateExecuteModal = ({ open, template, onClose, onExecute, executing = false }) => {
    const dispatch = useDispatch();
    const [accounts, setAccounts] = useState([]);
    const [targetAccount, setTargetAccount] = useState('');
    const [groups, setGroups] = useState([]);
    const [targetGroup, setTargetGroup] = useState('');
    const [startDate, setStartDate] = useState(new Date());
    const [dateOffset, setDateOffset] = useState({ startOffset: 0, dueOffset: 0 });
    const [preview, setPreview] = useState(null);
    const [fullTemplate, setFullTemplate] = useState(null);
    const [expandedProjects, setExpandedProjects] = useState(new Set());
    const [customVarValues, setCustomVarValues] = useState({});
    const [usedSlots, setUsedSlots] = useState([]);
    const [allUsers, setAllUsers] = useState([]);
    const [employeeSlots, setEmployeeSlots] = useState({});

    useEffect(() => {
        if (open) {
            loadAccounts();
            loadFullTemplate();
        }
    }, [open]);

    const customVarNames = useMemo(() => {
        if (!fullTemplate) return [];
        return extractCustomVars(collectTemplateTexts(fullTemplate));
    }, [fullTemplate]);

    useEffect(() => {
        if (customVarNames.length > 0) {
            setCustomVarValues((prev) => {
                const next = { ...prev };
                customVarNames.forEach((v) => { if (!(v in next)) next[v] = ''; });
                return next;
            });
        }
    }, [customVarNames]);

    useEffect(() => {
        if (!fullTemplate) return;
        const slots = collectUsedSlots(fullTemplate);
        setUsedSlots(slots);
        setEmployeeSlots({});
        if (slots.length > 0) {
            dispatch(getBillingEmployees((data) => setAllUsers((data || []).filter(a => a.active === true))));
        }
    }, [fullTemplate, dispatch]);

    useEffect(() => {
        if (targetAccount && template) {
            generatePreview();
        }
    }, [targetAccount, startDate, dateOffset, template, accounts, fullTemplate, customVarValues]);

    const canPickGroup = template?.templateType === 'project';

    useEffect(() => {
        setTargetGroup('');
        if (!canPickGroup || !targetAccount) {
            setGroups([]);
            return;
        }
        dispatch(getAccountGroups(targetAccount, (data) => {
            setGroups((data || []).filter((group) => !group.completedDate && !group.isDeleted));
        }));
    }, [canPickGroup, targetAccount, dispatch]);

    const loadAccounts = () => {
        dispatch(getAccounts(0, 0, null, false, (data) => {
            setAccounts(data || []);
        }));
    };

    const loadFullTemplate = () => {
        if (!template) return;
        if (template.templateType === 'group') {
            dispatch(getGroupTemplate(template._id, (data) => setFullTemplate(data)));
        } else if (template.templateType === 'project') {
            dispatch(getProjectTemplate(template._id, (data) => setFullTemplate(data)));
        }
    };

    const getTemplateVars = () => {
        const account = accounts.find(a => a._id === targetAccount);
        const date = new Date(startDate);
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];
        const monthShortNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
            'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const quarter = `Q${Math.floor(date.getMonth() / 3) + 1}`;

        return {
            accountName: account?.name || '',
            accountSlug: account?.slug || '',
            month: monthNames[date.getMonth()],
            monthShort: monthShortNames[date.getMonth()],
            monthNumber: String(date.getMonth() + 1).padStart(2, '0'),
            year: String(date.getFullYear()),
            yearShort: String(date.getFullYear()).slice(-2),
            day: String(date.getDate()).padStart(2, '0'),
            quarter,
            date: `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`,
        };
    };

    const generatePreview = () => {
        if (!targetAccount || !template) return;

        const vars = getTemplateVars();
        const previewName = processTemplateString(template.name, vars, customVarValues);
        const execDate = new Date(startDate);

        let calculatedStartDate = new Date(execDate);
        let calculatedDueDate = null;

        if (template.templateDateType === 'relative') {
            calculatedStartDate = resolveRelativeAnchor(template.templateStartRelative, execDate) || new Date(execDate);
            const startOff = template.templateStartOffset || 0;
            if (startOff) calculatedStartDate.setDate(calculatedStartDate.getDate() + startOff);

            const dueAnchor = resolveRelativeAnchor(template.templateDueRelative, execDate);
            const dueOff = template.templateDueOffset || 0;
            if (dueAnchor) {
                calculatedDueDate = dueAnchor;
                if (dueOff) calculatedDueDate.setDate(calculatedDueDate.getDate() + dueOff);
            } else if (dueOff) {
                calculatedDueDate = new Date(calculatedStartDate);
                calculatedDueDate.setDate(calculatedDueDate.getDate() + dueOff);
            }
        } else if (template.templateDateType === 'offset') {
            const offset = dateOffset.startOffset !== undefined ? dateOffset.startOffset : (template.templateStartOffset || 0);
            calculatedStartDate.setDate(calculatedStartDate.getDate() + offset);
            if (template.templateDueOffset) {
                calculatedDueDate = new Date(calculatedStartDate);
                const dueOff = dateOffset.dueOffset !== undefined ? dateOffset.dueOffset : (template.templateDueOffset || 0);
                calculatedDueDate.setDate(calculatedDueDate.getDate() + dueOff);
            }
        } else {
            if (template.dueDate || template.templateDueOffset) {
                calculatedDueDate = new Date(calculatedStartDate);
                const dueOff = dateOffset.dueOffset !== undefined ? dateOffset.dueOffset : (template.templateDueOffset || 0);
                calculatedDueDate.setDate(calculatedDueDate.getDate() + dueOff);
            }
        }

        setPreview({
            name: previewName,
            startDate: calculatedStartDate,
            dueDate: calculatedDueDate
        });
    };

    const handleExecute = () => {
        if (executing) return;
        const options = {
            targetAccount,
            startDate: toLocalDay(startDate)
        };

        if (canPickGroup && targetGroup) {
            options.targetGroup = targetGroup;
        }

        if (template.templateDateType === 'offset') {
            options.dateOffset = {
                startOffset: dateOffset.startOffset !== undefined ? dateOffset.startOffset : (template.templateStartOffset || 0),
                dueOffset: dateOffset.dueOffset !== undefined ? dateOffset.dueOffset : (template.templateDueOffset || 0)
            };
        }

        if (Object.keys(customVarValues).some((k) => customVarValues[k])) {
            options.customVars = customVarValues;
        }

        const filledSlots = Object.fromEntries(
            Object.entries(employeeSlots).filter(([, v]) => v)
        );
        if (Object.keys(filledSlots).length > 0) {
            options.employeeSlots = filledSlots;
        }

        onExecute(options);
    };

    const toggleProjectExpand = (projectId) => {
        setExpandedProjects(prev => {
            const next = new Set(prev);
            if (next.has(projectId)) next.delete(projectId);
            else next.add(projectId);
            return next;
        });
    };

    const vars = targetAccount ? getTemplateVars() : null;

    const renderStructurePreview = () => {
        if (!fullTemplate || !vars) return null;

        const projects = fullTemplate.projects || [];
        const tasks = fullTemplate.tasks || [];

        if (projects.length === 0 && tasks.length === 0) return null;

        return (
            <StructureBox>
                <StructureTitle variant="subtitle2">
                    This will create:
                </StructureTitle>
                <List dense disablePadding>
                    {template.templateType === 'group' && (
                        <StructureItem disablePadding>
                            <SmallIcon>
                                <FolderIcon fontSize="small" color="primary" />
                            </SmallIcon>
                            <ListItemText
                                primary={
                                    <InlineRow>
                                        <Typography variant="body2" fontWeight="bold">
                                            {processTemplateString(fullTemplate.name, vars, customVarValues)}
                                        </Typography>
                                        <TypeChip label="Group" size="small" color="primary" variant="outlined" />
                                    </InlineRow>
                                }
                            />
                        </StructureItem>
                    )}
                    {[...projects].sort((a, b) => a.name.localeCompare(b.name)).map((project) => {
                        const projTasks = project.tasks || [];
                        const isExpanded = expandedProjects.has(project._id);
                        return (
                            <React.Fragment key={project._id}>
                                <StructureClickableItem
                                    disablePadding
                                    indent={template.templateType === 'group' ? 3 : 0}
                                    clickable={projTasks.length > 0}
                                    onClick={() => projTasks.length > 0 && toggleProjectExpand(project._id)}
                                >
                                    {projTasks.length > 0 && (
                                        <TinyIcon>
                                            {isExpanded ? <SmallExpandIcon /> : <SmallChevronIcon />}
                                        </TinyIcon>
                                    )}
                                    <SmallIcon>
                                        <ProjectIcon fontSize="small" color="secondary" />
                                    </SmallIcon>
                                    <ListItemText
                                        primary={
                                            <InlineRow>
                                                <Typography variant="body2">
                                                    {processTemplateString(project.name, vars, customVarValues)}
                                                </Typography>
                                                <TypeChip label="Project" size="small" color="secondary" variant="outlined" />
                                                {projTasks.length > 0 && (
                                                    <TypeChip label={`${projTasks.length} tasks`} size="small" variant="outlined" />
                                                )}
                                            </InlineRow>
                                        }
                                    />
                                </StructureClickableItem>
                                {projTasks.length > 0 && (
                                    <Collapse in={isExpanded}>
                                        {projTasks.map((task) => (
                                            <StructureItem key={task._id} disablePadding indent={template.templateType === 'group' ? 8 : 5}>
                                                <SmallIcon>
                                                    <TaskIcon fontSize="small" color="success" />
                                                </SmallIcon>
                                                <ListItemText
                                                    primary={
                                                        <Typography variant="body2" color="text.secondary">
                                                            {processTemplateString(task.name, vars, customVarValues)}
                                                        </Typography>
                                                    }
                                                />
                                            </StructureItem>
                                        ))}
                                    </Collapse>
                                )}
                            </React.Fragment>
                        );
                    })}
                    {/* For project-level execution, show direct tasks */}
                    {template.templateType === 'project' && tasks.map((task) => (
                        <StructureItem key={task._id} disablePadding indent={3}>
                            <SmallIcon>
                                <TaskIcon fontSize="small" color="success" />
                            </SmallIcon>
                            <ListItemText
                                primary={
                                    <Typography variant="body2" color="text.secondary">
                                        {processTemplateString(task.name, vars, customVarValues)}
                                    </Typography>
                                }
                            />
                        </StructureItem>
                    ))}
                </List>
            </StructureBox>
        );
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>Execute Template: {template?.displayName || template?.name}</DialogTitle>
            <DialogContent>
                <ContentStack>
                    <Autocomplete
                        options={accounts}
                        getOptionLabel={(option) => option.name || ''}
                        value={accounts.find((a) => a._id === targetAccount) || null}
                        onChange={(_, newValue) => setTargetAccount(newValue?._id || '')}
                        renderInput={(params) => <TextField {...params} label="Target Account" required />}
                        isOptionEqualToValue={(option, value) => option._id === value._id}
                        fullWidth
                    />

                    {canPickGroup && (
                        <Autocomplete
                            options={groups}
                            getOptionLabel={(option) => option.name || ''}
                            value={groups.find((g) => g._id === targetGroup) || null}
                            onChange={(_, newValue) => setTargetGroup(newValue?._id || '')}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Group (optional)"
                                    helperText={
                                        !targetAccount
                                            ? 'Pick a target account first to see its groups.'
                                            : groups.length === 0
                                                ? 'This account has no open groups. The project will be created on its own.'
                                                : 'Leave empty to create the project outside any group.'
                                    }
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option._id === value._id}
                            disabled={!targetAccount || groups.length === 0}
                            fullWidth
                        />
                    )}

                    <AppDatePicker
                        label={template?.templateDateType === 'relative' ? 'Execution Date' : 'Start Date'}
                        value={toLocalDay(startDate)}
                        onChange={(v) => {
                            const parsed = parseDateInput(v);
                            if (parsed) setStartDate(parsed);
                        }}
                        helperText={template?.templateDateType === 'relative' ? 'Dates will be calculated relative to this date' : undefined}
                    />

                    {template?.templateDateType === 'relative' && (
                        <PreviewBox>
                            <Typography variant="body2" color="text.secondary" gutterBottom>Relative date resolution:</Typography>
                            {template.templateStartRelative && (() => {
                                const anchor = resolveRelativeAnchor(template.templateStartRelative, startDate);
                                const off = template.templateStartOffset || 0;
                                const resolved = anchor ? new Date(anchor) : null;
                                if (resolved && off) resolved.setDate(resolved.getDate() + off);
                                return (
                                    <Typography variant="body2">
                                        Start: {RELATIVE_ANCHOR_LABELS[template.templateStartRelative] || template.templateStartRelative}
                                        {off ? ` + ${off} days` : ''}
                                        {' '}&rarr; {formatDayShort(toLocalDay(resolved)) || '—'}
                                    </Typography>
                                );
                            })()}
                            {template.templateDueRelative && (() => {
                                const anchor = resolveRelativeAnchor(template.templateDueRelative, startDate);
                                const off = template.templateDueOffset || 0;
                                const resolved = anchor ? new Date(anchor) : null;
                                if (resolved && off) resolved.setDate(resolved.getDate() + off);
                                return (
                                    <Typography variant="body2">
                                        Due: {RELATIVE_ANCHOR_LABELS[template.templateDueRelative] || template.templateDueRelative}
                                        {off ? ` + ${off} days` : ''}
                                        {' '}&rarr; {formatDayShort(toLocalDay(resolved)) || '—'}
                                    </Typography>
                                );
                            })()}
                            {!template.templateDueRelative && (template.templateDueOffset > 0) && (() => {
                                const startAnchor = resolveRelativeAnchor(template.templateStartRelative, startDate);
                                const startOff = template.templateStartOffset || 0;
                                const resolvedStart = startAnchor ? new Date(startAnchor) : new Date(startDate);
                                if (startOff) resolvedStart.setDate(resolvedStart.getDate() + startOff);
                                const resolvedDue = new Date(resolvedStart);
                                resolvedDue.setDate(resolvedDue.getDate() + template.templateDueOffset);
                                return (
                                    <Typography variant="body2">
                                        Due: Start + {template.templateDueOffset} days &rarr; {formatDayShort(toLocalDay(resolvedDue))}
                                    </Typography>
                                );
                            })()}
                        </PreviewBox>
                    )}

                    {template?.templateDateType === 'offset' && (
                        <>
                            <TextField
                                label="Start Offset (days)"
                                type="number"
                                value={dateOffset.startOffset}
                                onChange={(e) => setDateOffset({ ...dateOffset, startOffset: parseInt(e.target.value) || 0 })}
                                fullWidth
                            />
                            <TextField
                                label="Due Offset (days)"
                                type="number"
                                value={dateOffset.dueOffset}
                                onChange={(e) => setDateOffset({ ...dateOffset, dueOffset: parseInt(e.target.value) || 0 })}
                                fullWidth
                            />
                        </>
                    )}

                    {usedSlots.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Employee Assignments</Typography>
                            <EmployeeSection>
                                {usedSlots.map((slot) => (
                                    <Autocomplete
                                        key={slot}
                                        options={allUsers}
                                        getOptionLabel={(u) => `${u.first_name || ''} ${u.last_name || ''}`.trim()}
                                        value={allUsers.find((u) => u._id === employeeSlots[slot]) || null}
                                        onChange={(_, val) => setEmployeeSlots((prev) => ({ ...prev, [slot]: val?._id || null }))}
                                        renderInput={(params) => <TextField {...params} label={`Employee ${slot}`} size="small" />}
                                        isOptionEqualToValue={(o, v) => o._id === v._id}
                                        fullWidth
                                    />
                                ))}
                            </EmployeeSection>
                        </Box>
                    )}

                    {customVarNames.length > 0 && (
                        <Box>
                            <Typography variant="subtitle2" gutterBottom>Custom Fields</Typography>
                            {customVarNames.map((varName) => (
                                <CustomVarField
                                    key={varName}
                                    fullWidth
                                    label={formatCustomVarLabel(varName)}
                                    value={customVarValues[varName] || ''}
                                    onChange={(e) => setCustomVarValues((prev) => ({ ...prev, [varName]: e.target.value }))}
                                    placeholder={`Value for {${varName}}`}
                                    size="small"
                                />
                            ))}
                        </Box>
                    )}

                    {preview && (
                        <>
                            <PreviewDivider />
                            <Typography variant="h6">Preview</Typography>
                            <PreviewBox>
                                <Typography variant="body1"><strong>Name:</strong> {preview.name}</Typography>
                                {canPickGroup && (
                                    <Typography variant="body2">
                                        <strong>Group:</strong> {groups.find((g) => g._id === targetGroup)?.name || 'None'}
                                    </Typography>
                                )}
                                <Typography variant="body2"><strong>Start Date:</strong> {formatDayShort(toLocalDay(preview.startDate))}</Typography>
                                {preview.dueDate && (
                                    <Typography variant="body2"><strong>Due Date:</strong> {formatDayShort(toLocalDay(preview.dueDate))}</Typography>
                                )}
                            </PreviewBox>

                            {renderStructurePreview()}
                        </>
                    )}

                    <TemplateVarsHelper>
                        <Typography variant="caption" color="text.secondary">
                            Template variables available: {'{account_name}'} {'{month}'} {'{year}'} {'{quarter}'} {'{day}'} {'{date}'} {'{month_short}'} {'{year_short}'}
                        </Typography>
                    </TemplateVarsHelper>
                </ContentStack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    onClick={handleExecute}
                    variant="contained"
                    disabled={!targetAccount || executing}
                    startIcon={executing ? <CircularProgress size={16} color="inherit" /> : null}
                >
                    {executing ? 'Executing…' : 'Execute'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default TemplateExecuteModal;
